import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Users, 
  UserPlus,
  Search, 
  Filter,
  Plus,
  Shield
} from 'lucide-react';
import { employeesAPI, permissionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import ConfirmationModal from './ConfirmationModal';

import EmployeeCard from './EmployeeCard';

const EmployeeAssignments = () => {
  const { token, isAuthenticated, employee: currentUser } = useAuth();
  const { employees: employeePerms } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    employee: null
  });

  // Check if current user is superadmin (developer)
  const isSuperAdmin = currentUser?.role === 'developer' || currentUser?.email === 'development@farmindie.com';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToCreate, goToEdit } = createNavigationHelpers('employees', navigate);

  // Fetch employees with permissions
  const { data: employeesData, isLoading: employeesLoading, error: employeesError } = useQuery(
    ['employees'],
    () => employeesAPI.getAll(),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        console.error('Employees fetch error:', error);
        toast.error('Failed to load employees');
      }
    }
  );

  // Delete employee mutation
  const deleteEmployeeMutation = useMutation(
    (uuid) => employeesAPI.delete(uuid),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['employees']);
        
        // Show detailed success message if backend provides deletion details
        if (response.data.deletedRecords) {
          const records = response.data.deletedRecords;
          const details = [];
          if (records.permissions > 0) details.push(`${records.permissions} permissions`);
          if (records.verificationTokens > 0) details.push(`${records.verificationTokens} tokens`);
          if (records.notifications > 0) details.push(`${records.notifications} notifications`);
          if (records.ordersUpdated > 0) details.push(`${records.ordersUpdated} orders updated`);
          
          const detailText = details.length > 0 ? ` (${details.join(', ')})` : '';
          toast.success(`Employee deleted successfully${detailText}`);
        } else {
          toast.success('Employee deleted successfully');
        }
        
        setDeleteConfirmation({ isOpen: false, employee: null });
      },
      onError: (error) => {
        console.error('Delete employee error:', error);
        toast.error(error.response?.data?.message || 'Failed to delete employee');
      }
    }
  );

  const handleEditEmployee = (employee) => {
    console.log('Editing employee:', employee);
    goToEdit(employee.uuid || employee.id);
  };

  const handleDeleteEmployee = (uuid) => {
    console.log('🗑️ Delete button clicked for UUID:', uuid);
    const employee = employees.find(emp => emp.uuid === uuid);
    console.log('🗑️ Found employee:', employee);
    console.log('🗑️ User permissions:', { canDelete: employeePerms.canDelete, role: currentUser?.role });
    
    // Double-check permissions
    if (!employeePerms.canDelete) {
      toast.error('You do not have permission to delete employees');
      return;
    }
    
    // Prevent deleting yourself
    if (employee?.uuid === currentUser?.uuid) {
      toast.error('You cannot delete your own account');
      return;
    }
    
    console.log('🗑️ Setting delete confirmation modal to open');
    setDeleteConfirmation({
      isOpen: true,
      employee: employee
    });
  };

  const confirmDeleteEmployee = () => {
    console.log('🗑️ Confirm delete called');
    console.log('🗑️ Employee to delete:', deleteConfirmation.employee);
    
    if (deleteConfirmation.employee) {
      const uuid = deleteConfirmation.employee.uuid;
      console.log('🗑️ Deleting employee with UUID:', uuid);
      deleteEmployeeMutation.mutate(uuid);
    } else {
      console.error('🗑️ No employee to delete');
    }
  };

  const cancelDeleteEmployee = () => {
    console.log('🗑️ Cancel delete called');
    setDeleteConfirmation({ isOpen: false, employee: null });
  };

  if (employeesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (employeesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load employees</div>
          <div className="text-gray-500 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  // Extract employees array from API response
  const employees = employeesData?.data?.data || employeesData?.data || [];

  // Filter employees based on search term, role, and status
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || employee.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && employee.is_active) ||
                         (statusFilter === 'inactive' && !employee.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
          <h4 className="font-semibold text-yellow-800 mb-2">🐛 Debug Info (Development Only)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-yellow-700">
            <div>
              <strong>User Role:</strong> {currentUser?.role || 'None'}
            </div>
            <div>
              <strong>Is SuperAdmin:</strong> {isSuperAdmin ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Can Create:</strong> {employeePerms.canCreate ? '✅' : '❌'}
            </div>
            <div>
              <strong>Can Update:</strong> {employeePerms.canUpdate ? '✅' : '❌'}
            </div>
            <div>
              <strong>Can Delete:</strong> {employeePerms.canDelete ? '✅' : '❌'}
            </div>
            <div>
              <strong>Total Employees:</strong> {employees.length}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Employee Management</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage employees and their module permissions
            </p>
          </div>
          {/* Add Employee Button - Only show if user can create employees */}
          {employeePerms.canCreate && (
            <button
              onClick={goToCreate}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-glow">
            <Filter className="h-5 w-5" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Search & Filter</h3>
          <div className="ml-auto flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span>{filteredEmployees.length} of {employees.length}</span>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all placeholder-gray-400 dark:placeholder-gray-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Role Filter</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full input-modern text-sm"
            >
              <option value="all">All Roles ({employees.length})</option>
              {isSuperAdmin && (
                <option value="developer">Developer ({employees.filter(emp => emp.role === 'developer').length})</option>
              )}
              <option value="admin">Admin ({employees.filter(emp => emp.role === 'admin').length})</option>
              <option value="manager">Manager ({employees.filter(emp => emp.role === 'manager').length})</option>
              <option value="staff">Staff ({employees.filter(emp => emp.role === 'staff').length})</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full input-modern text-sm"
            >
              <option value="all">All Status ({employees.length})</option>
              <option value="active">Active ({employees.filter(emp => emp.is_active).length})</option>
              <option value="inactive">Inactive ({employees.filter(emp => !emp.is_active).length})</option>
            </select>
          </div>
        </div>

        {/* Quick Filter Pills */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {setRoleFilter('all'); setStatusFilter('all'); setSearchTerm('');}}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Clear All Filters
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              statusFilter === 'active' 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Active Only
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              statusFilter === 'inactive' 
                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Inactive Only
          </button>
          <button
            onClick={() => setSearchTerm('')}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Show All
          </button>
        </div>
      </div>

      {/* Employee Cards */}
      <div className="animate-fade-in-up space-y-6" style={{ animationDelay: '0.4s' }}>
        {/* Employee Stats Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="card-modern text-center p-4">
            <div className="text-2xl sm:text-3xl font-bold gradient-text mb-1">{employees.length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Employees</div>
          </div>
          <div className="card-modern text-center p-4">
            <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-1">
              {employees.filter(emp => emp.is_active).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
          </div>
          <div className="card-modern text-center p-4">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">
              {employees.filter(emp => emp.is_verified).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Verified</div>
          </div>
          <div className="card-modern text-center p-4">
            <div className="text-2xl sm:text-3xl font-bold text-yellow-600 mb-1">
              {employees.filter(emp => !emp.is_verified).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
          </div>
        </div>

        {/* Employee Cards Grid - Fully Responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {filteredEmployees.map((employee, index) => (
            <div 
              key={employee.uuid} 
              className="animate-fade-in-up w-full" 
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <EmployeeCard
                employee={employee}
                onEdit={handleEditEmployee}
                onDelete={handleDeleteEmployee}
              />
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredEmployees.length === 0 && (
          <div className="card-modern text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-full flex items-center justify-center">
              <Users className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' 
                ? 'No matching employees found'
                : 'No employees yet'
              }
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' 
                ? 'Try adjusting your search criteria or filters to find the employees you\'re looking for.'
                : 'Start building your team by adding your first employee to the system.'
              }
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  onClick={() => {setSearchTerm(''); setRoleFilter('all'); setStatusFilter('all');}}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Filter className="w-4 h-4" />
                  <span>Clear All Filters</span>
                </button>
              )}
              
              {employeePerms.canCreate && (
                <button
                  onClick={goToCreate}
                  className="btn-primary flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Add First Employee</span>
                </button>
              )}
            </div>

            {/* Help Text */}
            {employees.length === 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                      <UserPlus className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span>Add team members</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <span>Assign permissions</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span>Manage your team</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={cancelDeleteEmployee}
        onConfirm={confirmDeleteEmployee}
        title="🗑️ Delete Employee Permanently"
        message={`❌ CRITICAL WARNING - PERMANENT DELETION ❌

You are about to PERMANENTLY DELETE:
👤 Employee: ${deleteConfirmation.employee?.name || 'Unknown'}
📧 Email: ${deleteConfirmation.employee?.email || 'Unknown'}
🔐 Role: ${deleteConfirmation.employee?.role?.toUpperCase() || 'Unknown'}
🆔 ID: ${deleteConfirmation.employee?.uuid?.slice(-8)?.toUpperCase() || 'Unknown'}

This will COMPLETELY REMOVE from database:
✅ Employee account and profile data
✅ All ${deleteConfirmation.employee?.permissions?.length || 0} assigned permissions
✅ Email verification status and tokens
✅ Password reset tokens and history
✅ All notifications sent to this employee
✅ Employee references in orders (set to NULL)
✅ All related authentication data

⚠️ THIS ACTION CANNOT BE UNDONE! ⚠️
⚠️ ALL DATA WILL BE PERMANENTLY LOST! ⚠️

Type "DELETE" to confirm this permanent action.`}
        confirmText={`🗑️ PERMANENTLY DELETE ${deleteConfirmation.employee?.name?.toUpperCase() || 'EMPLOYEE'}`}
        cancelText="❌ Cancel - Keep Employee"
        type="danger"
        isLoading={deleteEmployeeMutation.isLoading}
      />
    </div>
  );
};

export default EmployeeAssignments;